import {utils} from '@wallaby-ui/core';
import {
  cloneDeep,
  forEach,
  forOwn,
  has,
  isEmpty,
  isNil,
  keys,
  omit,
  sortBy,
  startsWith,
  trimStart,
} from 'lodash';

import {
  CONFLICT_HTTP_CODE,
  CONTAINER_INDEX_SCREEN_ID_SUFFIX,
  CONTEXT_PARAMS_DELIMITER,
} from 'src/js/containers/auto_layout/auto_layout_constants';
import {
  KEYWORD_DATA_CONTENT,
  KEYWORD_PROP_CONTENT_VALUE,
  KEYWORD_PROP_CONTENT_TO_STRING,
} from 'src/js/model/auto_layout_screen_data_helpers';

const AutoLayoutService = {
  getIndexScreenId(screenId, context = undefined) {
    const ctxString = AutoLayoutService.getContextToString(context);
    return `${screenId}.${CONTAINER_INDEX_SCREEN_ID_SUFFIX}${
      ctxString ? `.${ctxString}` : ''
    }`;
  },

  /**
   * Filters out some of contextParams that should not be involved in autoLayout environment.
   * @param {Object} contextParams
   */
  getFilteredContext(contextParams) {
    return omit(contextParams, 'caseType');
  },

  getContextToString(context) {
    let ctxString = '';
    const filteredContext = AutoLayoutService.getFilteredContext(context);
    if (filteredContext) {
      forEach(sortBy(keys(filteredContext)), (item) => {
        ctxString +=
          !isNil(filteredContext[item]) && filteredContext[item] !== ''
            ? `${item}${CONTEXT_PARAMS_DELIMITER}${filteredContext[item]}`
            : '';
        return ctxString;
      });
    }
    return ctxString;
  },

  getParentArrayScreenId(screenId, contextParams) {
    return AutoLayoutService.getIndexScreenId(
      AutoLayoutService.getCoreScreenId(screenId),
      contextParams
    );
  },

  getItemScreenId(screenId, item) {
    return `${screenId}.${item}`;
  },

  getIdFromScreenId(screenId) {
    return screenId.substring(screenId.indexOf('.') + 1);
  },

  /**
   * Gets core part of screenId.
   * The core part starts from start of the screenId and
   * ends at the first occurrence of child separator.
   * If the child separator is nor present the whole screenId is returned.
   * @param screenId
   * @returns {any}
   */
  getCoreScreenId(screenId) {
    const screenIdChildSeparatorIdx = screenId.indexOf('.');
    return screenIdChildSeparatorIdx >= 0
      ? screenId.substring(0, screenId.indexOf('.'))
      : screenId;
  },

  getComponentId(screenId, pathToData, idSuffix) {
    return `${screenId}${idSuffix || ''}${
      !isEmpty(pathToData) ? '.' : ''
    }${pathToData}`;
  },

  isConflictRequestResponse(error) {
    const errorCode = error
      ? error.status || error.statusText || error.message || error
      : '';
    return (
      !isEmpty(errorCode.toString()) &&
      startsWith(trimStart(errorCode.toString()), CONFLICT_HTTP_CODE.toString())
    );
  },

  /**
   * Evaluates toString of content object.
   * @param {Object} data Data with toString value.
   * @param {Function} compiledTemplate Compiled template that can interpolate data properties.
   * @returns {String} toString value with evaluated interpolated properties.
   */
  evaluateToString(data, compiledTemplate) {
    let ownData;
    try {
      // extract $value to the property, empty values resolve as ''
      ownData = cloneDeep(data);
      forOwn(ownData, (value, key) => {
        if (has(value, KEYWORD_PROP_CONTENT_VALUE)) {
          if (
            value[KEYWORD_PROP_CONTENT_VALUE] &&
            (value[KEYWORD_PROP_CONTENT_TO_STRING] ||
              value[KEYWORD_PROP_CONTENT_TO_STRING] === '')
          ) {
            ownData[key] = value[KEYWORD_PROP_CONTENT_TO_STRING];
          } else {
            ownData[key] = value[KEYWORD_PROP_CONTENT_VALUE] || '';
          }
        }
      });
      return compiledTemplate(ownData);
    } catch (e) {
      return '';
    }
  },

  /* eslint-disable no-param-reassign */
  /**
   * Sets toString value in data object. It modifies data object.
   * @param {Object} data Data object.
   * @param {String} toStringValue New value of toString.
   */
  setToString(data, toStringValue) {
    if (
      data[KEYWORD_PROP_CONTENT_TO_STRING] &&
      data[KEYWORD_PROP_CONTENT_TO_STRING][KEYWORD_PROP_CONTENT_VALUE]
    ) {
      data[KEYWORD_PROP_CONTENT_TO_STRING][KEYWORD_PROP_CONTENT_VALUE] =
        toStringValue;
    } else {
      data[KEYWORD_PROP_CONTENT_TO_STRING] = {
        [KEYWORD_PROP_CONTENT_VALUE]: toStringValue,
      };
    }
  },
  /* eslint-enable no-param-reassign */

  /**
   * Store (in data) toString value of core object from content.
   */
  reevaluateToStringFromData(data, toStringTemplateString) {
    const summaryTpl = utils.StringUtils.evalString(
      toStringTemplateString || 'toString not provided'
    );
    if (data[KEYWORD_DATA_CONTENT]) {
      const toStringValue = AutoLayoutService.evaluateToString(
        data[KEYWORD_DATA_CONTENT],
        summaryTpl
      );
      AutoLayoutService.setToString(data[KEYWORD_DATA_CONTENT], toStringValue);
    }
  },
};

export default AutoLayoutService;
